import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format currency with proper symbol. Zero (and nullish) values drop the symbol
// — "$0.00" is visually noisy, especially in dense tables/exports.
export function formatCurrency(amount: number, currency: string = 'USD', showDecimals: boolean = false): string {
  const decimals = showDecimals ? 2 : 0
  if (!amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(0)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

// Format date consistently
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d)
}

// Format date with time
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

// Format percentage
export function formatPercentage(value: number): string {
  return `${Math.round(value)}%`
}

// Compact number for chart axes: 3 → "3", 3_000 → "3K", 3_100_000 → "3.1M", 3_000_000 → "3M"
// CR-4352: Strip trailing ".0" so axis labels read "3M" not "3.0M"
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value)
  const format = (n: number) => {
    const s = n.toFixed(1)
    return s.endsWith('.0') ? s.slice(0, -2) : s
  }
  if (abs >= 1_000_000) return `${format(value / 1_000_000)}M`
  if (abs >= 1_000) return `${format(value / 1_000)}K`
  return format(value)
}

// Get initials from name
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
